import { ActiveSearchFilter, SearchDropdownForm, SearchItemConfig } from "src/components/Form/search-dropdown-form";
import { TransactionStatus, TransactionType } from "../../types/user-transaction.entity";
import { CurrencyCode } from "@utils/util.types";

type Props = {
  updateQuery: (filter: UsersTransactionsRouteState) => void;
  initialQuery?: UsersTransactionsRouteState;
};

export type UsersTransactionsRouteState = {
  sessionId?: string;
  user?: string;
  bookingId?: string;
  status?: TransactionStatus;
  type?: TransactionType;
  currency?: CurrencyCode;
};

type SearchKey = "session" | "user" | "booking" | "status" | "type" | "currency";

const searchItems: Record<SearchKey, SearchItemConfig> = {
  session: {
    type: "input",
    placeholder: "Enter a session ID",
  },
  user: {
    type: "input",
    placeholder: "Enter a user ID, email address, or phone number",
  },
  booking: {
    type: "input",
    placeholder: "Enter a booking ID",
  },
  status: {
    type: "select",
    placeholder: "All Status",
    options: [
      { value: "PENDING", text: "Pending" },
      { value: "FINALIZED", text: "Finalized" },
      { value: "CANCELLED", text: "Cancelled" },
      { value: "FAILED", text: "Failed" },
    ],
  },
  type: {
    type: "select",
    placeholder: "All Types",
    options: [
      { value: "CREDIT", text: "Credit" },
      { value: "DEBIT", text: "Debit" },
      { value: "RESERVING", text: "Reserving" },
      { value: "HOLDING", text: "Holding" },
    ],
  },
  currency: {
    type: "select",
    placeholder: "All",
    options: [
      { value: "KARMA", text: "karma" },
      { value: "EUR", text: "euro" },
    ],
  },
};

const labelMap: Record<keyof typeof searchItems, string> = {
  session: "Session",
  user: "User",
  booking: "Booking",
  status: "Status",
  type: "Type",
  currency: "Currency",
};

export const UsersTransactionsSearch = ({ updateQuery, initialQuery = {} }: Props) => {
  const handleSubmit = (key: SearchKey, value: string) => {
    const nextFilters: UsersTransactionsRouteState = { ...initialQuery };

    if (!value) {
      switch (key) {
        case "booking":
          delete nextFilters.bookingId;
          break;
        case "session":
          delete nextFilters.sessionId;
          break;
        case "user":
          delete nextFilters.user;
          break;
        case "status":
          delete nextFilters.status;
          break;
        case "type":
          delete nextFilters.type;
          break;
        case "currency":
          delete nextFilters.currency;
          break;
      }

      updateQuery(nextFilters);
      return;
    }

    switch (key) {
      case "booking":
        nextFilters.bookingId = value;
        break;
      case "session":
        nextFilters.sessionId = value;
        break;
      case "user":
        nextFilters.user = value;
        break;
      case "status":
        nextFilters.status = value as TransactionStatus;
        break;
      case "type":
        nextFilters.type = value as TransactionType;
        break;
      case "currency":
        nextFilters.currency = value as CurrencyCode;
        break;
    }

    updateQuery(nextFilters);
  };

  const activeFilters = Object.entries(initialQuery).reduce<ActiveSearchFilter<SearchKey>[]>((filters, [key, value]) => {
    if (!value) return filters;

    const searchKey =
      key === "bookingId" ? "booking" :
      key === "sessionId" ? "session" :
      key === "user" ? "user" :
      key;

    if (!(searchKey in searchItems)) return filters;

    const typedSearchKey = searchKey as keyof typeof searchItems;
    const config = searchItems[typedSearchKey];
    const rawValue = String(value);
    const resolvedValue =
      config.type === "select" ? config.options?.find((option) => option.value === rawValue)?.text || rawValue : rawValue;

    filters.push({
      key: typedSearchKey,
      label: labelMap[typedSearchKey],
      value: resolvedValue,
    });

    return filters;
  }, []);

  return (
    <div className="pt-5 pb-7">
      <SearchDropdownForm
        searchItems={searchItems}
        handleSubmit={handleSubmit}
        activeFilters={activeFilters}
        onRemoveFilter={(key) => handleSubmit(key, "")}
        onClearAll={() => updateQuery({})}
      />
    </div>
  );
};
